import { array } from 'fp-ts';
import { PortfolioCentralPaymentPolicyDetail } from '@mews-commander/data-access';
import { DividerList } from '@mews-commander/ui';
import { lt, showSuccessAlert } from '@mews/framework';
import { PropertiesSection, PropertyGroupsSection, TotalPropertiesSection } from '../../../../_shared/components';
import { usePaymentPolicyAssignProperty, usePaymentPolicyUnassignProperty } from '../../../mutations';
import { AssignPropertyContextProvider } from '../../../../_shared/contexts';
import { AssignPropertiesFormValues, toAssignPropertiesParameters } from '../../../../_shared/schemas';
import { AssignPropertiesSegment } from '../../../../_shared/types';
import { AssignPropertiesButton } from './AssignPropertiesButton';

interface PropertiesTabContentProps {
    data: PortfolioCentralPaymentPolicyDetail;
}

export const PropertiesTabContent = ({ data }: PropertiesTabContentProps) => {
    const { mutateAsync: mutateAssign } = usePaymentPolicyAssignProperty();
    const { mutateAsync: mutateUnassign } = usePaymentPolicyUnassignProperty();

    const assignProperties = async (values: AssignPropertiesFormValues) => {
        await mutateAssign(
            {
                centralPaymentPolicyId: data.id,
                ...toAssignPropertiesParameters(values),
            },
            {
                onSuccess: () => {
                    if (values.segment === AssignPropertiesSegment.PropertyGroups) {
                        showSuccessAlert({
                            title: lt('PolicyAssignedToPropertyGroupTitle'),
                            description: lt('PolicyAssignedToPropertyGroupDescription'),
                        });
                    } else {
                        showSuccessAlert({
                            title: lt('PolicyAssignedToPropertyTitle'),
                            description: lt('PolicyAssignedToPropertyDescription'),
                        });
                    }
                },
            }
        );
    };

    const unassignProperties = async ({
        enterpriseIds,
        propertyGroupIds,
    }: {
        enterpriseIds?: string[];
        propertyGroupIds?: string[];
    }) => {
        await mutateUnassign({
            propertyIds: enterpriseIds,
            propertyGroupIds,
            centralPaymentPolicyId: data.id,
        });
    };

    return (
        <DividerList spacing="400">
            <AssignPropertyContextProvider
                value={{
                    assignedPropertyGroupIds: data.propertyGroupRateGroupCounts?.map(({ id }) => id),
                    assignedPropertyIds: data.propertyRateGroupCounts?.map(({ id }) => id),
                    assignProperties,
                    unassignProperties,
                }}
            >
                <TotalPropertiesSection data={data} rightContent={<AssignPropertiesButton />} />
                {data.propertyGroupRateGroupCounts && array.isNonEmpty(data.propertyGroupRateGroupCounts) ? (
                    <PropertyGroupsSection data={data} />
                ) : null}
                {data.propertyRateGroupCounts && array.isNonEmpty(data.propertyRateGroupCounts) ? (
                    <PropertiesSection data={data} />
                ) : null}
            </AssignPropertyContextProvider>
        </DividerList>
    );
};
